import { z } from 'zod';
import {
  createModel,
  currencySchema,
  partySchema,
  QueryMany,
  QueryOne,
  refSchema,
  tenantSchema,
} from '../../common';
import { dateSchema, nameSchema, objectIdSchema, priceSchema } from '../../validation';
import {
  orderPricingStrategySchema,
} from '../order/order.payment.model';
import { contractRouteRefSchema } from '../order/order.contract.model';

/**
 * how a target row's price was set.
 *
 * - `directed`      - priced from a directed `Route` (isOwnRoute=false), with
 *                     or without `agreementId`. `pricing.route` is snapshotted.
 * - `seller_own`    - priced from the target's own seller-side Route
 *                     (isOwnRoute=true, buyer empty).
 * - `buyer_own`     - priced from the proposedBuyer's own buyer-side Route
 *                     (isOwnRoute=true, seller empty).
 * - `proposed`      - manually entered or overridden by the buyer. May still
 *                     carry `pricing.route` for audit when a Route existed
 *                     but was overridden (combined with
 *                     `pricingStrategy: 'override_route_directed_pricing'`).
 * - `bid_pending`   - public_load_board row awaiting open bids; `proposedTotal`
 *                     is absent until a bid is accepted. (Future.)
 */
export const offerPricingModeSchema = z.enum([
  'directed',
  'seller_own',
  'buyer_own',
  'proposed',
  'bid_pending',
]);
export type OfferPricingMode = z.infer<typeof offerPricingModeSchema>;

export const offerTargetPricingSchema = z.object({
  /** how this row's price was set. See {@link offerPricingModeSchema}. */
  mode: offerPricingModeSchema,
  /**
   * the price offered to this specific target. Absent ONLY when
   * `mode === 'bid_pending'` (open bidding scenario).
   */
  proposedTotal: priceSchema.optional(),
  currency: currencySchema,
  /** which pricing strategy applies. See `order.payment.model.ts`. */
  pricingStrategy: orderPricingStrategySchema.optional(),
  /**
   * audit context - the snapshot of the Route that priced this row. Kept
   * even when the buyer overrode the price, so we know the original baseline.
   * See `order.contract.model.ts` for the snapshot shape.
   */
  route: contractRouteRefSchema.optional(),
});
export type OfferTargetPricing = z.infer<typeof offerTargetPricingSchema>;

/**
 * per-target row state.
 *
 * Each target has its OWN state machine - a counter from carrier A never
 * affects the row for carrier B. The `responses[]` log captures every
 * transition for an audit trail.
 *
 * State transitions:
 *
 *   open --> accepted     (target accepts at the offered price)
 *   open --> declined     (target declines)
 *   open --> counter_proposed  (target counters with a different price)
 *   open --> expired      (top-level expiresAt elapsed)
 *   counter_proposed --> accepted    (buyer accepts the counter)
 *   counter_proposed --> declined    (buyer declines the counter)
 *   counter_proposed --> counter_proposed (buyer re-counters; replaces `counter`)
 *   open/counter_proposed --> withdrawn   (sibling row was accepted, or the
 *                                          buyer withdrew the whole Offer)
 */
export const offerTargetStatusSchema = z.enum([
  'open',
  'accepted',
  'declined',
  'counter_proposed',
  'expired',
  'withdrawn',
]);
export type OfferTargetStatus = z.infer<typeof offerTargetStatusSchema>;

/**
 * a single counter-proposal on one target row.
 *
 * Pattern B: per-row counter (not a separate Offer). Re-countering REPLACES
 * the previous counter - history of prior counters lives in `responses[]`.
 */
export const offerTargetCounterSchema = z.object({
  proposedPrice: priceSchema,
  /** who proposed the counter (typically the row's `party`, i.e. the seller side). */
  proposedBy: partySchema,
  at: dateSchema,
  note: z.string().optional(),
});
export type OfferTargetCounter = z.infer<typeof offerTargetCounterSchema>;

/**
 * an entry in a target row's audit log. Capped to ~20 entries by OfferService
 * writes to keep the doc lean during chatty counter rounds.
 */
export const offerTargetResponseActionSchema = z.enum([
  'viewed',
  'declined',
  'countered',
  'accepted',
  'withdrawn',
]);
export type OfferTargetResponseAction = z.infer<typeof offerTargetResponseActionSchema>;

export const offerTargetResponseSchema = z.object({
  action: offerTargetResponseActionSchema,
  at: dateSchema,
  /** who triggered the action. Use a super-admin `Party` snapshot for system events. */
  by: partySchema,
  note: z.string().optional(),
});
export type OfferTargetResponse = z.infer<typeof offerTargetResponseSchema>;

/**
 * one recipient row inside `Offer.targets[]`.
 *
 * One Offer per broadcast; one row per recipient. This lets:
 *   - per-carrier directed Route prices,
 *   - per-carrier overrides,
 *   - a single flat price across all carriers,
 * all coexist in the same Offer doc.
 *
 * Accept becomes a single Offer-doc `arrayFilters` update (winner=accepted,
 * siblings=withdrawn) + an Order `$push contracts[]` in the same transaction.
 */
export const offerTargetSchema = z.object({
  /** identity of this row inside `targets[]`. Surfaced in `acceptedTarget.targetId`. */
  _id: objectIdSchema,
  /** the recipient carrier/broker. */
  party: partySchema,

  pricing: offerTargetPricingSchema,

  status: offerTargetStatusSchema,

  /** Pattern B counter, scoped to THIS row only. Absent when never countered. */
  counter: offerTargetCounterSchema.optional(),

  /** per-target audit trail. Capped to ~20 entries by OfferService. */
  responses: z.array(offerTargetResponseSchema).default([]),

  /** present once this row is the winner. */
  acceptedAt: dateSchema.optional(),
});
export type OfferTarget = z.infer<typeof offerTargetSchema>;

/**
 * frozen snapshot of the parent Order's "what is being shipped" fields.
 *
 * Used to render the carrier inbox (and the future public load board) WITHOUT
 * needing to fetch the parent Order - one Offer query gives the full inbox
 * row. The snapshot is taken at Offer create-time and is NOT auto-refreshed
 * if the Order changes materially later (the proposedBuyer should withdraw
 * and re-create the Offer in that case).
 */
export const offerLoadSummaryRouteSchema = z.object({
  from: z.string().trim(),
  to: z.string().trim(),
});
export type OfferLoadSummaryRoute = z.infer<typeof offerLoadSummaryRouteSchema>;

export const offerLoadSummaryDatesSchema = z.object({
  pickup: dateSchema,
  dropoff: dateSchema.optional(),
});
export type OfferLoadSummaryDates = z.infer<typeof offerLoadSummaryDatesSchema>;

export const offerLoadSummaryGoodsSchema = z.object({
  /** broad goods type, e.g. "goods" | "container". */
  type: z.string().trim(),
  /** total weight in kg (rounded), absent when unknown. */
  weight: priceSchema.optional(),
  description: z.string().optional(),
});
export type OfferLoadSummaryGoods = z.infer<typeof offerLoadSummaryGoodsSchema>;

export const offerLoadSummarySchema = z.object({
  route: offerLoadSummaryRouteSchema,
  dates: offerLoadSummaryDatesSchema,
  goods: offerLoadSummaryGoodsSchema,
  /** preferred vehicle type, e.g. "flatbed" | "refrigerated_truck". */
  vehicleType: z.string().optional(),
});
export type OfferLoadSummary = z.infer<typeof offerLoadSummarySchema>;

/**
 * top-level rollup of the Offer.
 */
export const offerStatusSchema = z.enum([
  'open',
  'accepted',
  'closed',
  'expired',
  'withdrawn',
]);
export type OfferStatus = z.infer<typeof offerStatusSchema>;

/**
 * visibility / distribution mode of the Offer.
 */
export const offerVisibilitySchema = z.enum([
  'targeted',
  'private_network',
  'public_load_board',
]);
export type OfferVisibility = z.infer<typeof offerVisibilitySchema>;

/**
 * resolution record when a target row wins.
 */
export const offerAcceptedTargetSchema = z.object({
  targetId: objectIdSchema,
  party: partySchema,
  /** points at the resulting `Order.contracts[i].contractId`. */
  contractId: objectIdSchema,
  acceptedAt: dateSchema,
});
export type OfferAcceptedTarget = z.infer<typeof offerAcceptedTargetSchema>;

/**
 * a marketplace bid on a `public_load_board` Offer.
 */
export const offerBidSchema = z.object({
  _id: objectIdSchema,
  party: partySchema,
  price: priceSchema,
  at: dateSchema,
  note: z.string().optional(),
});
export type OfferBid = z.infer<typeof offerBidSchema>;

/**
 * `Offer` db model - SEPARATE collection from `Order`.
 */
export const OfferSchema = createModel({
  ref: refSchema,
  orderId: objectIdSchema,
  proposedBuyer: partySchema,
  visibility: offerVisibilitySchema,
  loadSummary: offerLoadSummarySchema,
  targets: z.array(offerTargetSchema).default([]),
  status: offerStatusSchema,
  acceptedTarget: offerAcceptedTargetSchema.optional(),
  bids: z.array(offerBidSchema).optional(),
  expiresAt: dateSchema.optional(),
  /**
   * auto-computed visibility set for the Offer. Built from the
   * proposed buyer + every target row party + late-binding contact
   * fields. Indexed `{ 'tenants.id': 1 }` so a carrier's open-offer
   * inbox is one seek per identifier.
   *
   * Required (default `[]`) — every Offer write goes through
   * `TenantsBuilder` and lands a fresh tenants array (Phase 2
   * deliverable).
   */
  tenants: z.array(tenantSchema).default([]),
});

export type Offer = z.infer<typeof OfferSchema.model>;
export type OfferData = z.infer<typeof OfferSchema.data>;
export type OfferPartial = z.infer<typeof OfferSchema.partial>;
export type OfferQueryMany = QueryMany<Offer>;
export type OfferQueryOne = QueryOne<Offer>;

/**
 * pricing context returned by `OfferService.previewBroadcast` for one
 * candidate target. NOT persisted - the buyer review UI consumes this and
 * confirms it into an `offerTargetPricingSchema` row in `OfferService.create`.
 */
export const offerBroadcastPreviewRowSchema = z.object({
  party: partySchema,
  name: nameSchema.optional(),
  /** snapshot of the Route the resolver would use for this row. */
  route: contractRouteRefSchema.optional(),
  suggestedPrice: priceSchema.optional(),
  suggestedMode: z.enum([
    'directed',
    'seller_own',
    'buyer_own',
    'needs_manual',
  ]),
});
export type OfferBroadcastPreviewRow = z.infer<typeof offerBroadcastPreviewRowSchema>;
